import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  StateStorage,
  StorageValue,
} from "zustand/middleware";
import {
  STOREFRONT_LINKS,
  STOREFRONT_LINKS_PREVIEW,
} from "../../constants/routes";
import { selectedTagsType } from "../stockList/StorefrontFilterStore";

type TabType = "all" | "specific";

type StorefrontLinkSearchStoreState = {
  pricingChecked: boolean;
  includePricing: boolean;
  selectedAudienceTab: TabType;
  selectedProductTab: TabType;
  selectedTags: selectedTagsType[];
  selectedCustomerId?: number;
  selectedPriceTierId?: number;
  selectedAvailabilityTierId?: number;
  selectedCustomerZoneId?: number;
  selectedProductId?: number;
};

type StorefrontLinkSearchStoreActions = {
  setPricingChecked: (pricingChecked: boolean) => void;
  setSelectedAudienceTab: (selectedAudienceTab: TabType) => void;
  setSelectedProductTab: (selectedProductTab: TabType) => void;
  setSelectedCustomerId: (selectedCustomerId: number | undefined) => void;
  setSelectedPriceTierId: (selectedPriceTierId: number | undefined) => void;
  setSelectedAvailabilityTierId: (
    selectedAvailabilityTierId: number | undefined,
  ) => void;
  setSelectedCustomerZoneId: (
    selectedCustomerZoneId: number | undefined,
  ) => void;
  setIncludePricing: (includePricing: boolean) => void;
  setSelectedProductId: (selectedProductId: number | undefined) => void;
  setSelectedTags: (selectedTags: selectedTagsType[]) => void;
  reset: () => void;
};

const initialStorefrontLinkSearchStoreState: StorefrontLinkSearchStoreState = {
  pricingChecked: false,
  selectedAudienceTab: "all",
  selectedProductTab: "all",
  selectedCustomerId: undefined,
  selectedPriceTierId: undefined,
  selectedAvailabilityTierId: undefined,
  selectedCustomerZoneId: undefined,
  includePricing: false,
  selectedProductId: undefined,
  selectedTags: [],
};

const getUrlSearch = () => {
  return window.location.search.slice(1);
};

export const getStorefrontLinkUrlParams = ({
  productId,
  customerId,
  selectedProductTab,
  selectedAudienceTab,
  pricingChecked,
}: {
  productId?: number;
  customerId?: number;
  selectedProductTab: TabType;
  selectedAudienceTab: TabType;
  pricingChecked: boolean;
}) => {
  const storefrontLinkSearchStore = {
    pricingChecked,
    ...(selectedAudienceTab && {
      selectedAudienceTab,
    }),
    ...(selectedProductTab && {
      selectedProductTab,
    }),
    ...(productId && {
      selectedProductId: productId,
    }),
    ...(customerId && {
      selectedCustomerId: customerId,
    }),
  };
  const encoded = encodeURIComponent(JSON.stringify(storefrontLinkSearchStore));
  return `${STOREFRONT_LINKS}?storefrontLinkSearchStore=${encoded}`;
};

export const getStoredValues = (key: string) => {
  const searchParams = new URLSearchParams(getUrlSearch());
  const storedValue = searchParams.get(key);
  if (!storedValue) return null;
  return storedValue;
};

const hashStorage: StateStorage = {
  getItem: (key) => {
    return getStoredValues(key);
  },
  setItem: (key, newValueString): void => {
    if (
      window.location.pathname === STOREFRONT_LINKS ||
      window.location.pathname === STOREFRONT_LINKS_PREVIEW
    ) {
      const searchParams = new URLSearchParams(getUrlSearch());
      const newValue = JSON.parse(newValueString) as StorageValue<
        Partial<StorefrontLinkSearchStoreState>
      >;
      const route =
        window.location.pathname === STOREFRONT_LINKS
          ? STOREFRONT_LINKS
          : STOREFRONT_LINKS_PREVIEW;
      searchParams.set(key, JSON.stringify(newValue.state));
      window.history.replaceState(
        null,
        "",
        `${route}?${searchParams.toString()}`,
      );
    }
  },
  removeItem: (key): void => {
    const searchParams = new URLSearchParams(getUrlSearch());
    searchParams.delete(key);
    window.location.search = searchParams.toString();
  },
};

const key = "storefrontLinkSearchStore";
export const useStorefrontLinkSearchStore = create<
  StorefrontLinkSearchStoreState & StorefrontLinkSearchStoreActions
>()(
  persist(
    (set) => {
      const storedValues = getStoredValues(key);
      const initialParsedData = storedValues
        ? (JSON.parse(storedValues) as Partial<StorefrontLinkSearchStoreState>)
        : undefined;
      return {
        ...initialStorefrontLinkSearchStoreState,
        ...(initialParsedData
          ? {
              ...initialParsedData,
              pricingChecked:
                Boolean(initialParsedData.selectedPriceTierId) ||
                Boolean(initialParsedData.pricingChecked),
            }
          : {}),
        setPricingChecked: (pricingChecked) => set({ pricingChecked }),
        setSelectedAudienceTab: (selectedAudienceTab) =>
          set({ selectedAudienceTab }),
        setSelectedProductTab: (selectedProductTab) =>
          set({ selectedProductTab }),
        setSelectedCustomerId: (selectedCustomerId) =>
          set({ selectedCustomerId }),
        setSelectedPriceTierId: (selectedPriceTierId) =>
          set({
            selectedPriceTierId,
            ...(selectedPriceTierId ? { pricingChecked: true } : {}),
          }),
        setSelectedAvailabilityTierId: (selectedAvailabilityTierId) =>
          set({ selectedAvailabilityTierId }),
        setSelectedCustomerZoneId: (selectedCustomerZoneId) =>
          set({ selectedCustomerZoneId }),
        setIncludePricing: (includePricing) => set({ includePricing }),
        setSelectedTags: (selectedTags: selectedTagsType[]) =>
          set({ selectedTags }),
        setSelectedProductId: (selectedProductId) => set({ selectedProductId }),
        reset: () => set(initialStorefrontLinkSearchStoreState),
      };
    },
    {
      name: key,
      storage: createJSONStorage(() => hashStorage),
      partialize: (state) => ({
        pricingChecked: state.pricingChecked,
        selectedAudienceTab: state.selectedAudienceTab,
        selectedProductTab: state.selectedProductTab,
        selectedCustomerId: state.selectedCustomerId,
        selectedPriceTierId: state.selectedPriceTierId,
        selectedAvailabilityTierId: state.selectedAvailabilityTierId,
        selectedCustomerZoneId: state.selectedCustomerZoneId,
        includePricing: state.includePricing,
        selectedProductId: state.selectedProductId,
      }),
    },
  ),
);
